import {
  createSession,
  hostedUiLogoutUrl,
  localStorageTokenStore,
  navigateTo,
  redirectToSignIn,
  takeTokensFromUrl,
  type SessionOptions,
} from "@jkast90/kast-common/auth";
import { refreshSession, revokeRefreshToken } from "@jkast90/kast-common/refresh-session";
import { getDeploymentConfig } from "../deployment-config";

/**
 * The two roles, as the API defines them (ADMIN_GROUPS and
 * SITE_MANAGER_GROUPS in api/src/config.rs), which
 * lower-case both sides, so every hasGroup and RequireGroup here passes
 * ignoreCase: the Cognito group is "HNT".
 *
 * - Full admins (`admin`) run the site and the system.
 * - Site managers (`HNT`) run the site: submissions, gallery, locations,
 *   shop, orders and the summary.
 */
export const HNT_FULL_ADMIN_GROUPS = ["admin"] as const;
export const HNT_SITE_MANAGER_GROUPS = ["hnt"] as const;

/** Either role: who gets the Admin link and the admin pages. */
export const HNT_ADMIN_GROUPS = [...HNT_SITE_MANAGER_GROUPS, ...HNT_FULL_ADMIN_GROUPS] as const;

/**
 * The site's sign-in session, under the `auth_*` keys it has always used, so
 * signed-in admins stay signed in. `userEmail` is the old slice's key, cleared
 * on sign-out. The Cognito calls are passed in explicitly so tests can mock
 * `refresh-session`.
 */
export function createAppSession(options: Partial<SessionOptions> = {}) {
  const { cognitoDomain, clientId } = getDeploymentConfig();
  return createSession({
    cognitoDomain,
    clientId,
    store: localStorageTokenStore({ alsoClear: ["userEmail"] }),
    exchange: refreshSession,
    revoke: revokeRefreshToken,
    ...options,
  });
}

export const session = createAppSession();

/** To login.kast-dev.net, coming back to this page (or `returnTo`, if it's on this site). */
export function signIn(returnTo?: string): void {
  redirectToSignIn({ signInUrl: `${getDeploymentConfig().cognitoUiUrl}/signin`, returnTo });
}

/** Revoke, clear, then end the hosted UI's session too (or go home without one). */
export function signOut(): Promise<void> {
  const { cognitoDomain, clientId } = getDeploymentConfig();
  return session.signOut({
    leave: () =>
      navigateTo(
        cognitoDomain && clientId
          ? hostedUiLogoutUrl({ cognitoDomain, clientId, logoutUri: window.location.origin })
          : "/",
      ),
  });
}

/**
 * Back from sign-in: store the hash's tokens, scrub them from the URL, and
 * put the user on the page they left. Call before the first render.
 */
export function acceptSignInFromUrl(): void {
  const taken = takeTokensFromUrl(session);
  if (taken?.returnTo) window.history.replaceState(null, "", taken.returnTo);
}
